<template>
  <ReportWrapper :is-downloading="isFetching" @download="downloadPdf">
    <template #prepend>
      <v-menu
        v-model="filterMenu"
        content-class="reset-overflow-menu"
        offset-y
        :close-on-content-click="false"
        :max-width="1440"
      >
        <template #activator="{ on, attrs }">
          <v-btn
            v-bind="attrs"
            :text="!$vuetify.breakpoint.xs && !$vuetify.breakpoint.sm"
            :outlined="$vuetify.breakpoint.xs || $vuetify.breakpoint.sm"
            color="primary"
            v-on="on"
          >
            <CfsIcon class="mr-2" max-height="20px" max-width="20px" icon-name="filter" />
            {{ $t('Filters') }}
          </v-btn>
        </template>
        <v-card class="px-4 x-sm:!pt-0 sm:py-4 modal-filter-block">
          <v-row class="mb-4">
            <v-col cols="12" sm="6" md="4">
              <FilterOptionAutoComplete
                v-model="formModel.service"
                service="invoice"
                label="Service"
                query-type="service"
              />
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <CustomerAutocompleteComponent
                v-model="formModel.billTo"
                item-value="accountNumber"
                others
                :return-object="false"
                customer
                label="Bill To"
              />
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <CfsDateField v-model="formModel.createdDate" range label="Created Date" />
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <CfsDateField v-model="formModel.dueDate" range label="Due Date" />
            </v-col>
          </v-row>
          <v-divider></v-divider>
          <v-row class="mt-4">
            <v-col class="d-flex justify-space-between align-center" cols="12">
              <div>
                <span
                  class="waterloo--text text-m font-weight-medium cursor-pointer"
                  @click="onResetFilters"
                >
                  Reset filter
                </span>
              </div>
              <div>
                <v-btn outlined color="title" class="mr-2" @click="onClose"
                  >{{ $t('Cancel') }}
                </v-btn>
                <v-btn color="primary" @click="onSubmit">{{ $t('Done') }}</v-btn>
              </div>
            </v-col>
          </v-row>
        </v-card>
      </v-menu>
    </template>
  </ReportWrapper>
</template>

<script lang="ts">
import { defineAsyncComponent, defineComponent } from 'vue';
import { useReports } from '@/composables/reports/useReports';
import { downloadFile } from '@/helpers/download';
import useForm from '@/composables/forms/useForm';
import FilterOptionAutoComplete from '@/components/autocomplete/FilterOptionAutoComplete.vue';
import { useToggle } from '@/composables/useToggle';
import ReportWrapper from '@/components/reports/ReportWrapper.vue';

export default defineComponent({
  name: 'ReportInvoice',
  components: {
    ReportWrapper,
    FilterOptionAutoComplete,
    CustomerAutocompleteComponent: defineAsyncComponent(
      () => import('@/components/autocomplete/CustomerAutocompleteComponent.vue')
    ),
  },
  setup() {
    const { formModel, resetFormModel } = useForm({
      service: null,
      billTo: null,
      createdDate: null,
      dueDate: null,
    });

    const { useDownloadInvoiceReport } = useReports();
    const { refetch: downloadReport, isFetching } = useDownloadInvoiceReport(formModel);

    const downloadPdf = async () => {
      if (isFetching.value) return;
      const { data } = await downloadReport();

      if (!data) return;

      downloadFile(data.base64Data, data.filename ?? 'Invoice Report');
    };

    const [filterMenu, toggleFilterMenu] = useToggle();

    const onResetFilters = () => {
      resetFormModel();
      onSubmit();
    };

    const onSubmit = async () => {
      onClose();
    };

    const onClose = () => {
      toggleFilterMenu(false);
    };

    return { filterMenu, formModel, isFetching, onResetFilters, onSubmit, onClose, downloadPdf };
  },
});
</script>
